import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { usersApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import SearchInput from "../../components/common/SearchInput";
import PageHeader from "../../components/common/PageHeader";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: "owner" | "admin" | "accountant" | "resident";
  status: "active" | "inactive" | "invited";
  lastLogin?: string;
  units?: string[];
  createdAt: string;
}

interface UserFilters {
  search: string;
  role: string;
  status: string;
}

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilters>({
    search: "",
    role: "",
    status: "",
  });

  const { currentBuilding } = useBuilding();

  // Role options for filtering
  const roleOptions = [
    { value: "", label: "All Roles" },
    { value: "owner", label: "Owner" },
    { value: "admin", label: "Admin" },
    { value: "accountant", label: "Accountant" },
    { value: "resident", label: "Resident" },
  ];

  // Status options for filtering
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "invited", label: "Invited" },
  ];

  useEffect(() => {
    const fetchUsers = async () => {
      if (!currentBuilding?.id) return;

      try {
        setLoading(true);
        const data = await usersApi.getAll(currentBuilding.id);
        setUsers(data);
        setFilteredUsers(data);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentBuilding?.id]);

  useEffect(() => {
    if (users.length > 0) {
      let result = [...users];

      // Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        result = result.filter(
          (user) =>
            user.firstName.toLowerCase().includes(searchTerm) ||
            user.lastName.toLowerCase().includes(searchTerm) ||
            user.email.toLowerCase().includes(searchTerm) ||
            (user.units &&
              user.units.some((unit) =>
                unit.toLowerCase().includes(searchTerm),
              )),
        );
      }

      // Apply role filter
      if (filters.role) {
        result = result.filter((user) => user.role === filters.role);
      }

      // Apply status filter
      if (filters.status) {
        result = result.filter((user) => user.status === filters.status);
      }

      setFilteredUsers(result);
    }
  }, [users, filters]);

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      role: "",
      status: "",
    });
  };

  // Calculate summary data
  const calculateSummary = () => {
    const total = filteredUsers.length;
    const active = filteredUsers.filter((u) => u.status === "active").length;
    const inactive = filteredUsers.filter(
      (u) => u.status === "inactive",
    ).length;
    const invited = filteredUsers.filter((u) => u.status === "invited").length;
    const owners = filteredUsers.filter((u) => u.role === "owner").length;
    const admins = filteredUsers.filter((u) => u.role === "admin").length;
    const accountants = filteredUsers.filter(
      (u) => u.role === "accountant",
    ).length;
    const residents = filteredUsers.filter((u) => u.role === "resident").length;

    return {
      total,
      active,
      inactive,
      invited,
      owners,
      admins,
      accountants,
      residents,
    };
  };

  const summary = calculateSummary();

  if (loading) {
    return <Spinner message="Loading users..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div className="">
      <PageHeader
        title="Users"
        description={`Manage users for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <Link
            to="/dashboard/users/invite"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Invite User
          </Link>
        }
      />

      {/* Filters */}
      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-4 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Filters
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Filter users by role or status.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-6">
                <SearchInput
                  placeholder="Search by name, email, or unit..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onClear={() => handleFilterChange("search", "")}
                />
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-gray-700"
                >
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.role}
                  onChange={(e) => handleFilterChange("role", e.target.value)}
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Summary
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Users
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {summary.total}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Active Users
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-green-600">
                  {summary.active}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Invited
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-yellow-600">
                  {summary.invited}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Inactive
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-500">
                  {summary.inactive}
                </dd>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6 flex flex-col items-center">
                <dt className="text-sm font-medium text-gray-500">
                  Role Breakdown
                </dt>
                <dd className="mt-4 flex justify-around w-full">
                  <div className="text-center">
                    <span className="block text-xl font-semibold text-primary-600">
                      {summary.owners}
                    </span>
                    <span className="block text-sm text-gray-500">Owners</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-xl font-semibold text-primary-600">
                      {summary.admins}
                    </span>
                    <span className="block text-sm text-gray-500">Admins</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-xl font-semibold text-primary-600">
                      {summary.accountants}
                    </span>
                    <span className="block text-sm text-gray-500">
                      Accountants
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="block text-xl font-semibold text-primary-600">
                      {summary.residents}
                    </span>
                    <span className="block text-sm text-gray-500">
                      Residents
                    </span>
                  </div>
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="mt-8">
        {filteredUsers.length === 0 ? (
          <EmptyState
            title="No users found"
            description={
              filters.search || filters.role || filters.status
                ? "No users match your search criteria."
                : "You don't have any users yet."
            }
            actionText={
              filters.search || filters.role || filters.status
                ? "Clear filters"
                : "Invite your first user"
            }
            actionHref={
              filters.search || filters.role || filters.status
                ? undefined
                : "/dashboard/users/invite"
            }
            onAction={
              filters.search || filters.role || filters.status
                ? handleClearFilters
                : undefined
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            }
          />
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <li key={user.id}>
                  <Link
                    to={`/dashboard/users/${user.id}`}
                    className="block hover:bg-gray-50"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500">
                              <span className="font-medium leading-none text-white">
                                {user.firstName.charAt(0)}
                                {user.lastName.charAt(0)}
                              </span>
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-primary-600 truncate">
                                {user.firstName} {user.lastName}
                              </p>
                              <div className="ml-2 flex-shrink-0 flex">
                                <p
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                  ${
                                    user.status === "active"
                                      ? "bg-green-100 text-green-800"
                                      : user.status === "invited"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {user.status.charAt(0).toUpperCase() +
                                    user.status.slice(1)}
                                </p>
                              </div>
                            </div>
                            <div className="mt-1 flex items-center">
                              <p className="text-sm text-gray-500">
                                {user.email}
                              </p>
                              <div className="ml-2 flex-shrink-0 flex">
                                <p
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                  ${
                                    user.role === "owner"
                                      ? "bg-purple-100 text-purple-800"
                                      : user.role === "admin"
                                      ? "bg-primary-100 text-primary-800"
                                      : user.role === "accountant"
                                      ? "bg-indigo-100 text-indigo-800"
                                      : "bg-green-100 text-green-800"
                                  }`}
                                >
                                  {user.role.charAt(0).toUpperCase() +
                                    user.role.slice(1)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="ml-2 flex flex-col flex-shrink-0 items-end">
                          {user.units && user.units.length > 0 && (
                            <div className="text-sm text-gray-500">
                              {user.units.join(", ")}
                            </div>
                          )}
                          {user.lastLogin && (
                            <div className="mt-1 text-xs text-gray-400">
                              Last login:{" "}
                              {new Date(user.lastLogin).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersList;
